import { allowsEmptyValue, isOperationNull } from 'helpers/filterHelper';
import { FILTER_CONNECTOR } from 'modules/decision/constants';
import {
    type FilterGroupT,
    type FilterNodeT,
} from 'modules/decision/types/explore';

/**
 * Returns true if the node is a group, false if it is a leaf.
 */
export const isFilterGroup = <Leaf>(
    node: FilterNodeT<Leaf>,
): node is FilterGroupT<Leaf> =>
    typeof node === 'object' && node !== null && 'connector' in node;

export const createEmptyFilterTree = <Leaf>(): FilterGroupT<Leaf> => ({
    connector: FILTER_CONNECTOR.and,
    children: [],
});

/**
 * Returns all leaves in the tree.
 */
export const getFilterLeaves = <Leaf>(tree: FilterGroupT<Leaf>): Leaf[] =>
    tree.children.flatMap((child) =>
        isFilterGroup(child) ? getFilterLeaves(child) : [child],
    );

/**
 * Returns the paths to all leaves in the tree. Each path is an array of indices,
 * where each index corresponds to a child in the tree at that level.
 *
 * @example
 * tree = AND [ leafA, OR [ leafB, leafC ] ]
 * => [[0], [1, 0], [1, 1]]
 *    leafA -> children[0]
 *    leafB -> children[1].children[0]
 *    leafC -> children[1].children[1]
 */
export const getFilterLeafPaths = <Leaf>(
    tree: FilterGroupT<Leaf>,
    prefix: number[] = [],
): number[][] =>
    tree.children.flatMap((child, index) =>
        isFilterGroup(child)
            ? getFilterLeafPaths(child, [...prefix, index])
            : [[...prefix, index]],
    );

/**
 * Returns the node at the given path in the tree, or undefined if the path is invalid.
 */
const nodeAtPath = <Leaf>(
    tree: FilterGroupT<Leaf>,
    path: number[],
): FilterNodeT<Leaf> | undefined => {
    let node: FilterNodeT<Leaf> = tree;
    for (const index of path) {
        if (!isFilterGroup(node)) return undefined;
        node = node.children[index];
        if (node === undefined) return undefined;
    }
    return node;
};

/**
 * Applies a transformation to all leaves in the tree, preserving the group structure.
 */
export const mapTree = <A, B>(
    tree: FilterGroupT<A>,
    transform: (leaf: A) => B,
): FilterGroupT<B> => ({
    connector: tree.connector,
    children: tree.children.map((child) =>
        isFilterGroup(child) ? mapTree(child, transform) : transform(child),
    ),
});

/**
 * Prunes empty groups from the tree. A group is considered empty if it has no children.
 */
export const pruneEmptyGroups = <Leaf>(
    tree: FilterGroupT<Leaf>,
): FilterGroupT<Leaf> => ({
    connector: tree.connector,
    children: tree.children.reduce<FilterNodeT<Leaf>[]>((kept, child) => {
        if (!isFilterGroup(child)) return [...kept, child];
        const pruned = pruneEmptyGroups(child);
        return pruned.children.length > 0 ? [...kept, pruned] : kept;
    }, []),
});

/**
 * Prunes leaves from the tree based on a predicate function.
 * A leaf is kept if the predicate returns true for it.
 */
export const pruneFilterLeaves = <Leaf>(
    tree: FilterGroupT<Leaf>,
    keep: (leaf: Leaf) => boolean,
): FilterGroupT<Leaf> =>
    pruneEmptyGroups({
        connector: tree.connector,
        children: tree.children.flatMap((child): FilterNodeT<Leaf>[] => {
            if (isFilterGroup(child)) return [pruneFilterLeaves(child, keep)];
            return keep(child) ? [child] : [];
        }),
    });

/**
 * Returns true if a leaf can be transformed into a group at the given path in the tree.
 * A leaf can be transformed into a group if its parent is the root or a group (path length <= 2).
 */
export const canTransformToGroup = (leafPath: number[]) => leafPath.length <= 2;

const invertConnector = (connector: FilterConnectorT): FilterConnectorT =>
    connector === FILTER_CONNECTOR.and
        ? FILTER_CONNECTOR.or
        : FILTER_CONNECTOR.and;

/**
 * Replaces the node at the specified path in the tree with a new node generated by the provided replace function.
 */
const replaceNodeAtPath = <Leaf>(
    tree: FilterGroupT<Leaf>,
    path: number[],
    replace: (node: FilterNodeT<Leaf>) => FilterNodeT<Leaf>,
): FilterGroupT<Leaf> => {
    if (path.length === 0) {
        const replaced = replace(tree);
        return isFilterGroup(replaced) ? replaced : tree;
    }
    const [index, ...rest] = path;
    return {
        ...tree,
        children: tree.children.map((child, i) => {
            if (i !== index) return child;
            if (rest.length === 0) return replace(child);
            return isFilterGroup(child)
                ? replaceNodeAtPath(child, rest, replace)
                : child;
        }),
    };
};

/**
 * Sets the connector of the group at the specified path in the tree to the provided connector.
 */
export const setConnectorAtPath = <Leaf>(
    tree: FilterGroupT<Leaf>,
    groupPath: number[],
    connector: FilterConnectorT,
): FilterGroupT<Leaf> =>
    replaceNodeAtPath(tree, groupPath, (node) =>
        isFilterGroup(node) ? { ...node, connector } : node,
    );

/**
 * Adds a leaf to the group at the specified path in the tree.
 */
export const addLeafAtPath = <Leaf>(
    tree: FilterGroupT<Leaf>,
    groupPath: number[],
    leaf: Leaf,
): FilterGroupT<Leaf> =>
    replaceNodeAtPath(tree, groupPath, (node) =>
        isFilterGroup(node)
            ? { ...node, children: [...node.children, leaf] }
            : node,
    );

/**
 * Updates the leaf at the specified path in the tree using the provided update function.
 */
export const changeLeafAtPath = <Leaf>(
    tree: FilterGroupT<Leaf>,
    leafPath: number[],
    update: (leaf: Leaf) => Leaf,
): FilterGroupT<Leaf> =>
    replaceNodeAtPath(tree, leafPath, (node) =>
        isFilterGroup(node) ? node : update(node),
    );

const removeChildAt = <Leaf>(
    tree: FilterGroupT<Leaf>,
    path: number[],
): FilterGroupT<Leaf> => {
    const parentPath = path.slice(0, -1);
    const index = path.at(-1);
    return replaceNodeAtPath(tree, parentPath, (node) =>
        isFilterGroup(node)
            ? { ...node, children: node.children.filter((_, i) => i !== index) }
            : node,
    );
};

/**
 * Removes the node at the specified path in the tree and prunes any empty groups that result from this removal.
 */
export const removeNodeAtPath = <Leaf>(
    tree: FilterGroupT<Leaf>,
    path: number[],
): FilterGroupT<Leaf> =>
    path.length === 0 ? tree : pruneEmptyGroups(removeChildAt(tree, path));

/**
 * Transforms a leaf at the specified path in the tree into a group,
 * preserving the leaf's value and inverting the parent's connector.
 */
export const transformLeafToGroup = <Leaf>(
    tree: FilterGroupT<Leaf>,
    leafPath: number[],
): FilterGroupT<Leaf> => {
    const parent = nodeAtPath(tree, leafPath.slice(0, -1));
    const parentConnector = isFilterGroup(parent)
        ? parent.connector
        : FILTER_CONNECTOR.and;
    return replaceNodeAtPath(tree, leafPath, (node) =>
        isFilterGroup(node)
            ? node
            : { connector: invertConnector(parentConnector), children: [node] },
    );
};

/**
 * Splits a group at the specified path in the tree into its children,
 * effectively removing the group and promoting its children to the parent's level.
 */
export const splitGroupAtPath = <Leaf>(
    tree: FilterGroupT<Leaf>,
    groupPath: number[],
): FilterGroupT<Leaf> => {
    const group = nodeAtPath(tree, groupPath);
    const index = groupPath.at(-1);
    if (index === undefined || !isFilterGroup(group)) return tree;
    return replaceNodeAtPath(tree, groupPath.slice(0, -1), (node) =>
        isFilterGroup(node)
            ? {
                  ...node,
                  children: [
                      ...node.children.slice(0, index),
                      ...group.children,
                      ...node.children.slice(index + 1),
                  ],
              }
            : node,
    );
};

type SerializableLeaf = Pick<
    QueryFilterT,
    'field' | 'operation' | 'value' | 'valueScale'
>;

/**
 * Parses a filter expression from the backend into a filter tree structure used in the frontend.
 */
export const parseFilterTree = (
    filterExpression?: ChartFilterExpressionGroupT | null,
): FilterGroupT<SentenceFilterT> => {
    if (!filterExpression) return createEmptyFilterTree();

    let leafCount = 0;
    const parseNode = (
        node: ChartFilterExpressionNodeT,
    ): FilterNodeT<SentenceFilterT> => {
        if ('connector' in node) {
            return {
                connector: node.connector,
                children: node.children.map(parseNode),
            };
        }
        leafCount += 1;
        return {
            field: node.field,
            operation: node.operation,
            value: node.values?.length ? node.values.join('|') : null,
            valueScale: node.valueScale,
            type: node.type,
            displayIndex: leafCount,
        };
    };

    return {
        connector: filterExpression.connector,
        children: filterExpression.children.map(parseNode),
    };
};

/**
 * Serializes a filter tree structure used in the frontend into a filter expression format expected by the backend.
 */
export const serializeFilterTree = (
    tree: FilterGroupT<SerializableLeaf>,
): DataQueryFilterExpressionT | null => {
    const serializeNode = (
        node: FilterNodeT<SerializableLeaf>,
    ): DataQueryFilterT | DataQueryFilterExpressionT | null => {
        if (isFilterGroup(node)) {
            const children = node.children
                .map(serializeNode)
                .filter((child) => child !== null);
            return children.length > 0
                ? { connector: node.connector, children }
                : null;
        }
        if (!node.field) return null;
        if (!allowsEmptyValue(node.operation) && !node.value) return null;
        const filter: DataQueryFilterT = {
            field: node.field,
            operation: node.operation,
            valueScale: node.valueScale,
        };
        if (!isOperationNull(node.operation) && node.value)
            filter.values = node.value.split('|');
        return filter;
    };

    const root = serializeNode(tree);
    return root !== null && 'connector' in root ? root : null;
};

export const serializeFilterTreeForQuery = (
    tree?: FilterGroupT<SerializableLeaf> | null,
): DataQueryFilterExpressionT | undefined =>
    tree ? (serializeFilterTree(tree) ?? undefined) : undefined;
